import React, { useCallback, useId, useMemo, useState } from 'react';
import { IconButton, InputAdornment, OutlinedInput, InputLabel, FormControl } from '@mui/material';
import type { InputProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Base type combining standard HTML input attributes with Material-UI InputProps.
 */
type BaseInputProps = React.InputHTMLAttributes<HTMLInputElement> & InputProps;

/**
 * Props for the InputWithClear component.
 * Supports both controlled and uncontrolled usage patterns.
 */
export interface InputWithClearProps extends Omit<BaseInputProps, 'onChange' | 'value'> {
  autoFocus?: boolean;
  /** Accessible label for the clear button */
  clearIconAriaLabel?: string;
  defaultValue?: string;
  fullWidth?: boolean;
  /** Hide the clear button entirely */
  hideClear?: boolean;
  /** The visible label text rendered above the input.
   *  Pass an empty string to omit the visible label — in that case you MUST
   *  supply `aria-label` so the input still has an accessible name (4.1.2). */
  label?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  placeholder?: string;
  startAdornment?: React.ReactNode;
  value?: string;
}

/**
 * A text input with an optional ✕ clear button, built on MUI OutlinedInput.
 *
 * Supports controlled and uncontrolled modes. The clear button appears
 * automatically when the field has a value.
 *
 * Accessibility
 * ─────────────
 * - When `label` is non-empty, the rendered <InputLabel> is wired to the
 *   input via htmlFor/id (we generate a stable id with useId).
 * - When `label` is empty (e.g. nav search), the caller is responsible for
 *   passing `aria-label` so the input still has a programmatic name.
 *
 * @example
 * // Controlled
 * <InputWithClear value={q} onChange={e => setQ(e.target.value)} onClear={() => setQ('')} />
 *
 * @example
 * // Without visible label — caller supplies aria-label
 * <InputWithClear label="" aria-label="Search journals" value={q} onChange={...} />
 */
const InputWithClear: React.FC<InputWithClearProps> = ({
  autoFocus = false,
  clearIconAriaLabel = 'Clear',
  defaultValue,
  fullWidth = true,
  hideClear = false,
  label = 'Search',
  onChange,
  onClear,
  placeholder = 'Search...',
  startAdornment,
  value,
  id: idProp,
  ...restProps
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const isControlled = value !== undefined;
  const displayValue = isControlled ? value : internalValue;

  // Stable id so InputLabel htmlFor and the input id can be wired together
  // even when the caller doesn't pass one explicitly.
  const generatedId = useId();
  const inputId = idProp ?? generatedId;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    },
    [isControlled, onChange],
  );

  const handleClear = useCallback(() => {
    if (!isControlled) setInternalValue('');
    onClear?.();
  }, [isControlled, onClear]);

  const endAdornment = useMemo(() => {
    if (!displayValue || hideClear) return undefined;
    return (
      <InputAdornment position="end">
        <IconButton
          aria-label={clearIconAriaLabel}
          edge="end"
          onClick={handleClear}
          size="small"
          type="button"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    );
  }, [displayValue, hideClear, clearIconAriaLabel, handleClear]);

  return (
    <FormControl variant="outlined" fullWidth={fullWidth}>
      {label && <InputLabel htmlFor={inputId}>{label}</InputLabel>}
      <OutlinedInput
        {...restProps}
        id={inputId}
        autoFocus={autoFocus}
        endAdornment={endAdornment}
        fullWidth={fullWidth}
        label={label || undefined}
        onChange={handleChange}
        placeholder={placeholder}
        startAdornment={
          startAdornment ? (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ) : undefined
        }
        sx={{
          '& .MuiInputAdornment-root': { bgcolor: 'transparent !important' },
          '& .MuiOutlinedInput-input': { bgcolor: 'transparent !important' },
          ...((restProps as { sx?: object }).sx ?? {}),
        }}
        value={displayValue}
      />
    </FormControl>
  );
};

export default React.memo(InputWithClear);
